/**
 * API client for Vibe Market Mini App.
 *
 * Demo mode is enabled only when API URL is not configured.
 */

import { getApiBaseUrl } from '../config/api';
import { getLastErrors, getLastRequest, getLastRequests, trackedFetch } from './fetcher';
import { getBuildStampCached } from './buildStamp';

const API_BASE_URL = getApiBaseUrl();
const TOKEN_KEY = 'vibe_market_token';

export type ApiErrorKind = 'network' | 'http' | 'cors' | 'unknown';

export interface ApiRequestMeta {
  url: string;
  method: string;
  startTs: number;
  endTs: number | null;
  durationMs: number | null;
}

export interface ApiErrorInfo {
  kind: ApiErrorKind;
  status?: number;
  message: string;
  request?: ApiRequestMeta;
  errorReportRequestId?: string;
}

/**
 * Check if API mode is enabled.
 */
export function isApiEnabled(): boolean {
  return API_BASE_URL !== null;
}

/**
 * Return resolved API base URL used by requests.
 */
export function getResolvedApiBaseUrl(): string | null {
  return API_BASE_URL;
}

/**
 * Get stored auth token.
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token.
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Clear auth token.
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * API error class.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly code: string;
  readonly request?: ApiRequestMeta;
  readonly errorReportRequestId?: string;

  constructor(params: {
    kind: ApiErrorKind;
    message: string;
    status?: number;
    code?: string;
    request?: ApiRequestMeta;
    errorReportRequestId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.kind = params.kind;
    this.status = params.status;
    this.code = params.code || 'API_ERROR';
    this.request = params.request;
    this.errorReportRequestId = params.errorReportRequestId;
  }
}

/**
 * Convert unknown errors into safe diagnostics object.
 */
export function getApiErrorInfo(error: unknown): ApiErrorInfo {
  if (error instanceof ApiError) {
    return {
      kind: error.kind,
      status: error.status,
      message: error.message,
      request: error.request,
      errorReportRequestId: error.errorReportRequestId,
    };
  }
  if (error instanceof Error) {
    return {
      kind: 'unknown',
      message: error.message,
    };
  }
  return {
    kind: 'unknown',
    message: 'Unknown error',
  };
}

function isCrossOriginUrl(requestUrl: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const targetOrigin = new URL(requestUrl).origin;
    return targetOrigin !== window.location.origin;
  } catch {
    return false;
  }
}

function classifyFetchError(error: unknown, requestUrl: string): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof TypeError) {
    const message = error.message || 'Request failed';
    const failedToFetch = message.toLowerCase().includes('failed to fetch');
    const likelyCors = failedToFetch && isCrossOriginUrl(requestUrl);

    return new ApiError({
      kind: likelyCors ? 'cors' : 'network',
      code: likelyCors ? 'CORS_OR_NETWORK' : 'NETWORK_ERROR',
      message: message,
    });
  }

  if (error instanceof Error) {
    return new ApiError({
      kind: 'unknown',
      code: 'UNKNOWN_ERROR',
      message: error.message,
    });
  }

  return new ApiError({
    kind: 'unknown',
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error',
  });
}

const CLIENT_LOG_PATH = '/debug/client-log';
const CLIENT_LOG_TIMEOUT_MS = 1200;

function safeLocationSnapshot(): {
  href: string;
  origin: string;
  pathname: string;
  search: string;
  hadHash: boolean;
  hadTgWebAppData: boolean;
} {
  if (typeof window === 'undefined') {
    return { href: '', origin: '', pathname: '', search: '', hadHash: false, hadTgWebAppData: false };
  }
  const origin = window.location.origin;
  const pathname = window.location.pathname;
  const search = window.location.search;
  const hash = String(window.location.hash || '');
  const hadHash = hash.length > 0;
  const hadTgWebAppData = hash.toLowerCase().includes('tgwebappdata=');
  return { href: `${origin}${pathname}${search}`, origin, pathname, search, hadHash, hadTgWebAppData };
}

function redact(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => redact(item));
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(obj)) {
      const lower = key.toLowerCase();
      if (
        lower.includes('initdata') ||
        lower.includes('authorization') ||
        lower.includes('bearer') ||
        lower.includes('token') ||
        lower.includes('cookie') ||
        lower.includes('password') ||
        lower.includes('secret')
      ) {
        out[key] = '[redacted]';
        continue;
      }
      out[key] = redact(nested);
    }
    return out;
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (
      lower.includes('tgwebappdata=') ||
      lower.includes('auth_date=') ||
      lower.includes('signature=') ||
      lower.includes('hash=') ||
      lower.includes('bearer ')
    ) {
      return '[redacted]';
    }
  }
  return value;
}

async function postClientLogRaw(payload: unknown): Promise<{ requestId: string | null } | null> {
  if (!API_BASE_URL) {
    return null;
  }

  const url = `${API_BASE_URL}${CLIENT_LOG_PATH}`;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId =
    controller && typeof window !== 'undefined'
      ? window.setTimeout(() => controller.abort(), CLIENT_LOG_TIMEOUT_MS)
      : null;

  try {
    const response = await trackedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(redact(payload)),
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: controller?.signal,
    });

    const text = await response.text();
    try {
      const parsed = JSON.parse(text) as { request_id?: unknown };
      const requestId = typeof parsed?.request_id === 'string' ? parsed.request_id.trim() : null;
      return { requestId: requestId || null };
    } catch {
      return { requestId: null };
    }
  } catch {
    return null;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

function shouldReportHttpStatus(status: number): boolean {
  return status >= 500;
}

async function maybeReportApiError(params: {
  error: ApiError;
  request: ApiRequestMeta;
  phase: 'fetch' | 'http' | 'parse';
}): Promise<string | null> {
  // Never recurse.
  try {
    const path = new URL(params.request.url).pathname;
    if (path === CLIENT_LOG_PATH) {
      return null;
    }
  } catch {
    // ignore
  }

  const payload = {
    ts: new Date().toISOString(),
    type: 'auto-error-report',
    phase: params.phase,
    build: getBuildStampCached(),
    location: safeLocationSnapshot(),
    userAgent: String(typeof navigator !== 'undefined' ? navigator.userAgent || '' : '').slice(0, 220),
    request: params.request,
    error: {
      name: params.error.name,
      kind: params.error.kind,
      code: params.error.code,
      status: params.error.status ?? null,
      message: params.error.message,
      stack: params.error.stack ? String(params.error.stack).slice(0, 6000) : null,
    },
    lastRequest: getLastRequest(),
    lastRequests: getLastRequests().slice(0, 10),
    lastErrors: getLastErrors().slice(0, 10),
  };

  const res = await postClientLogRaw(payload);
  return res?.requestId || null;
}

/**
 * Format API error detail (FastAPI returns string or array of validation objects).
 */
function formatApiDetailMessage(detail: unknown, status: number): string {
  if (detail == null) {
    return `Request failed with status ${status}`;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object')
      .map((item) => {
        const msg = item.msg;
        if (typeof msg === 'string') return msg;
        const loc = item.loc;
        if (Array.isArray(loc) && loc.length > 1) {
          const field = loc[loc.length - 1];
          return typeof field === 'string' ? `${field}: ${String(msg || 'error')}` : String(msg || 'error');
        }
        return String(msg || 'validation error');
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join('. ') : `Request failed with status ${status}`;
  }
  if (typeof detail === 'object' && 'message' in (detail as object)) {
    return String((detail as { message: unknown }).message);
  }
  return `Request failed with status ${status}`;
}

/**
 * Request options.
 */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

/**
 * Make an API request.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!API_BASE_URL) {
    throw new ApiError({
      kind: 'unknown',
      code: 'API_NOT_CONFIGURED',
      message: 'API URL is not configured',
    });
  }

  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  // Only send Content-Type when we actually send a JSON body.
  // This reduces noisy preflights in constrained WebViews.
  const requestHeaders: Record<string, string> = { ...headers };
  if (body !== undefined && requestHeaders['Content-Type'] === undefined) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  if (!skipAuth) {
    const token = getToken();
    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${API_BASE_URL}${normalizedPath}`;
  const init: RequestInit = {
    method,
    headers: requestHeaders,
    // Telegram Mini App should never rely on cookies/credentials.
    // These defaults also reduce cross-site noise and help with deterministic debugging.
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const startTs = Date.now();
  const baseMeta: ApiRequestMeta = {
    url,
    method,
    startTs,
    endTs: null,
    durationMs: null,
  };

  let response: Response;
  try {
    response = await trackedFetch(url, init);
  } catch (error) {
    const endTs = Date.now();
    const requestMeta: ApiRequestMeta = {
      ...baseMeta,
      endTs,
      durationMs: Math.max(0, endTs - startTs),
    };

    const classified = classifyFetchError(error, url);
    const apiError = new ApiError({
      kind: classified.kind,
      status: classified.status,
      code: classified.code,
      message: classified.message,
      request: requestMeta,
    });

    const reportId = await maybeReportApiError({ error: apiError, request: requestMeta, phase: 'fetch' });
    if (reportId) {
      throw new ApiError({
        kind: apiError.kind,
        status: apiError.status,
        code: apiError.code,
        message: apiError.message,
        request: requestMeta,
        errorReportRequestId: reportId,
      });
    }

    throw apiError;
  }

  const endTs = Date.now();
  const requestMeta: ApiRequestMeta = {
    ...baseMeta,
    endTs,
    durationMs: Math.max(0, endTs - startTs),
  };

  if (!response.ok) {
    let errorData: { detail?: unknown; code?: string } = {};
    try {
      errorData = (await response.json()) as { detail?: unknown; code?: string };
    } catch {
      errorData = {};
    }

    const message = formatApiDetailMessage(errorData.detail, response.status);

    const apiError = new ApiError({
      kind: 'http',
      status: response.status,
      code: errorData.code || `HTTP_${response.status}`,
      message,
      request: requestMeta,
    });

    if (shouldReportHttpStatus(response.status)) {
      const reportId = await maybeReportApiError({ error: apiError, request: requestMeta, phase: 'http' });
      if (reportId) {
        throw new ApiError({
          kind: apiError.kind,
          status: apiError.status,
          code: apiError.code,
          message: apiError.message,
          request: requestMeta,
          errorReportRequestId: reportId,
        });
      }
    }

    throw apiError;
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    const apiError = new ApiError({
      kind: 'unknown',
      code: 'INVALID_JSON',
      message: 'Invalid JSON response',
      request: requestMeta,
    });

    const reportId = await maybeReportApiError({ error: apiError, request: requestMeta, phase: 'parse' });
    if (reportId) {
      throw new ApiError({
        kind: apiError.kind,
        status: apiError.status,
        code: apiError.code,
        message: apiError.message,
        request: requestMeta,
        errorReportRequestId: reportId,
      });
    }

    throw apiError;
  }
}

// =============================================================================
// Auth API
// =============================================================================

interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    telegram_id: number;
    username: string | null;
    full_name: string | null;
    role?: UserRole;
    is_admin: boolean;
  };
}

/**
 * Authenticate with Telegram initData.
 */
export async function authenticate(initData: string): Promise<AuthResponse> {
  const response = await request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: { initData },
    skipAuth: true,
  });

  setToken(response.access_token);
  return response;
}

export interface MeUser {
  telegram_id: number;
  username: string | null;
  full_name: string | null;
  role?: UserRole;
  is_admin: boolean;
}

export type UserRole = 'user' | 'moderator' | 'admin';

export function isModeratorOrAdmin(user: Pick<MeUser, 'role' | 'is_admin'> | null | undefined): boolean {
  const role = String(user?.role || '').toLowerCase();
  return Boolean(user?.is_admin) || role === 'moderator' || role === 'admin';
}

/**
 * Get current user info (requires auth).
 */
export async function getMe(): Promise<MeUser> {
  return request<MeUser>('/me');
}

// =============================================================================
// Payments API (Telegram Stars)
// =============================================================================

export interface StarsCreateInvoiceResponse {
  order_id: string;
  invoice_link: string;
  amount_xtr: number;
  title: string;
  status: string;
  reused_recent_pending_order: boolean;
}

export interface PaymentOrderStatus {
  order_id: string;
  payload: string;
  status: 'pending' | 'prechecked' | 'paid' | 'failed' | 'refunded' | 'canceled' | string;
  product_code: string;
  title: string;
  amount_xtr: number;
  currency: string;
  paid_at: string | null;
  access_granted: boolean;
}

export interface AccessEntitlement {
  entitlement_key: string;
  product_code: string;
  grant_type: 'subscription' | 'feature_flag' | 'quota' | string;
  is_active: boolean;
  value_int: number | null;
  expires_at: string | null;
  source_order_id: string | null;
}

export interface AccessSnapshot {
  telegram_id: number;
  entitlements: AccessEntitlement[];
}

export async function createStarsInvoice(params: {
  initData: string;
  productCode: string;
}): Promise<StarsCreateInvoiceResponse> {
  return request<StarsCreateInvoiceResponse>('/payments/stars/create-invoice', {
    method: 'POST',
    body: {
      initData: params.initData,
      product_code: params.productCode,
    },
    skipAuth: true,
  });
}

export async function createStarsDonationInvoice(params: {
  initData: string;
  amountXtr: number;
}): Promise<StarsCreateInvoiceResponse> {
  return request<StarsCreateInvoiceResponse>('/payments/stars/create-donation-invoice', {
    method: 'POST',
    body: {
      initData: params.initData,
      amount_xtr: Math.trunc(params.amountXtr),
    },
    skipAuth: true,
  });
}

export async function getPaymentOrderStatus(orderId: string): Promise<PaymentOrderStatus> {
  return request<PaymentOrderStatus>(`/payments/orders/${encodeURIComponent(orderId)}`);
}

export async function getPaymentOrderStatusByPayload(payload: string): Promise<PaymentOrderStatus> {
  return request<PaymentOrderStatus>(
    `/payments/orders/by-payload/${encodeURIComponent(payload)}`,
  );
}

export async function getMyAccessSnapshot(): Promise<AccessSnapshot> {
  return request<AccessSnapshot>('/payments/access/me');
}

// =============================================================================
// Public app config
// =============================================================================

export interface PublicAppConfig {
  bot_username: string | null;
}

export async function getPublicAppConfig(): Promise<PublicAppConfig> {
  const res = await request<{ bot_username?: string | null }>('/public/app-config', {
    skipAuth: true,
  });
  const normalized = typeof res.bot_username === 'string'
    ? res.bot_username.trim().replace(/^@+/, '')
    : '';
  return { bot_username: normalized || null };
}

// =============================================================================
// Referrals API (Phase 3)
// =============================================================================

export interface ProcessReferralResponse {
  recorded: boolean;
  message: string;
}

/**
 * Record that the current user was referred by referrer_telegram_id (from start_param ref_XXX). Idempotent.
 */
export async function processReferral(referrerTelegramId: number): Promise<ProcessReferralResponse> {
  return request<ProcessReferralResponse>('/referrals/process', {
    method: 'POST',
    body: { referrer_telegram_id: referrerTelegramId },
  });
}

/**
 * Get number of users referred by the current user.
 */
export async function getReferralCount(): Promise<number> {
  const res = await request<{ count: number }>('/referrals/count');
  return res.count ?? 0;
}

// =============================================================================
// Uploads
// =============================================================================

/**
 * Upload cover image. Accepts jpg, png, webp, max 5MB.
 * Returns public URL to use as cover_image_url.
 */
export async function uploadCoverImage(file: File): Promise<{ url: string }> {
  if (!API_BASE_URL) {
    throw new ApiError({
      kind: 'unknown',
      code: 'API_NOT_CONFIGURED',
      message: 'API URL is not configured',
    });
  }
  const form = new FormData();
  form.append('file', file);
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await trackedFetch(`${API_BASE_URL}/uploads/cover`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });
  const text = await response.text();
  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const data = JSON.parse(text) as { detail?: string };
      if (data.detail) detail = data.detail;
    } catch {
      // ignore
    }
    throw new ApiError({
      kind: 'http',
      status: response.status,
      code: `HTTP_${response.status}`,
      message: detail,
    });
  }
  try {
    return JSON.parse(text || '{}') as { url: string };
  } catch {
    throw new ApiError({
      kind: 'unknown',
      code: 'INVALID_JSON',
      message: 'Invalid JSON response from upload',
    });
  }
}

// =============================================================================
// Projects API
// =============================================================================

export interface Project {
  id: string;
  status: 'draft' | 'submitted' | 'needs_fix' | 'approved' | 'rejected' | 'published';
  revision: number;
  title_short: string;
  completion_percent: number;
  next_action: {
    action: string;
    label: string;
    cta_enabled: boolean;
  };
  can_edit: boolean;
  can_submit: boolean;
  can_archive: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  rejected_reason?: string | null;
  published_at?: string | null;
  tg_post_url?: string | null;
  current_step: string | null;
  missing_fields: string[];

  // Public publishing (MVP)
  published?: boolean;
  public_slug?: string | null;
  show_contacts?: boolean;
  cover_image_url?: string | null;
  views_count?: number;
  reactions_count?: number;
}

export interface ProjectDetails {
  id: string;
  status: 'draft' | 'submitted' | 'needs_fix' | 'approved' | 'rejected' | 'published';
  revision: number;
  author_id?: number | null;
  current_step: string | null;
  answers: Record<string, unknown> | null;
  fields: Record<string, unknown>;
  preview_html: string | null;
  completion_percent: number;
  missing_fields: string[];
  filled_fields: string[];
  next_action: {
    action: string;
    label: string;
    cta_enabled: boolean;
  };
  can_edit: boolean;
  can_submit: boolean;
  can_archive: boolean;
  can_delete: boolean;
  approved_at?: string | null;
  rejected_at?: string | null;
  rejected_reason?: string | null;
  tg_post_url?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;

  // Public publishing (MVP)
  published?: boolean;
  published_at?: string | null;
  public_slug?: string | null;
  show_contacts?: boolean;
  cover_image_url?: string | null;
  views_count?: number;
  reactions_count?: number;
  want_count?: number;
  idea_count?: number;
  invest_count?: number;
  viewer_reaction_type?: 'want' | 'idea' | 'invest' | null;
}

// =============================================================================
// Showcase (GET /projects) — cursor pagination, no auth
// =============================================================================

export interface ShowcaseAuthor {
  id: number;
  username: string | null;
  avatar_url: string | null;
  rating: number | null;
}

export interface ShowcaseProject {
  id: string;
  emoji: string | null;
  title: string;
  category: string | null;
  project_status?: string | null;
  statuses: string[];
  tools: string[];
  dev_time: string | null;
  price: string | null;
  views_count: number;
  reactions_count: number;
  want_count?: number;
  idea_count?: number;
  invest_count?: number;
  created_at: string;
  author: ShowcaseAuthor;
}

export interface ShowcaseProjectsResponse {
  items: ShowcaseProject[];
  next_cursor: string | null;
}

export interface FetchShowcaseParams {
  cursor?: string | null;
  limit?: number;
  category?: string | null;
  statuses?: string[];
  search?: string | null;
}

/**
 * List published projects for showcase (cursor pagination, filters). No auth.
 */
export async function fetchShowcaseProjects(params: FetchShowcaseParams = {}): Promise<ShowcaseProjectsResponse> {
  const { cursor, limit = 20, category, statuses, search } = params;
  const q = new URLSearchParams();
  q.set('limit', String(Math.max(1, Math.min(100, limit ?? 20))));
  if (cursor) q.set('cursor', cursor);
  if (category && category.trim()) q.set('category', category.trim());
  if (statuses?.length) statuses.forEach((s) => q.append('statuses', s));
  if (search && search.trim()) q.set('search', search.trim());
  return request<ShowcaseProjectsResponse>(`/projects?${q.toString()}`, { skipAuth: true });
}

/**
 * Get current user's projects.
 */
export async function getProjects(): Promise<Project[]> {
  return request<Project[]>('/projects/my');
}

/**
 * Create a new draft project.
 */
export async function createDraft(): Promise<ProjectDetails> {
  return request<ProjectDetails>('/projects/create_draft', {
    method: 'POST',
  });
}

/**
 * Get project details by ID.
 */
export async function getProject(id: string): Promise<ProjectDetails> {
  return request<ProjectDetails>(`/projects/${id}`);
}

export interface ProjectPatch {
  project_title?: string | null;
  problem?: string | null;
  audience_type?: string | null;
  niche?: string | null;
  goal?: string | null;
  author_name?: string | null;
  author_contact_mode?: 'telegram' | 'email' | 'phone' | string | null;
  author_contact_value?: string | null;
  cover_image_url?: string | null;
  what_done?: string | null;
  project_status?: string | null;
  dev_time?: string | null;
  potential?: string | null;
  inbound_ready?: string | null;
  emoji?: string | null;
  category?: string | null;
  tools?: string[] | null;
  statuses?: string[] | null;
  price?: string | null;
  site_url?: string | null;
  demo_url?: string | null;
  github_url?: string | null;
  tg_url?: string | null;
  // Legacy aliases for backward compatibility with older clients.
  website_url?: string | null;
  repo_url?: string | null;
}

/**
 * Partially update project fields by ID.
 */
export async function patchProject(id: string, patch: ProjectPatch): Promise<ProjectDetails> {
  return request<ProjectDetails>(`/projects/${id}`, {
    method: 'PATCH',
    body: patch,
  });
}

export interface PublicProjectListItem {
  id: string;
  slug: string | null;
  public_id: string;
  public_url: string;
  published_at: string | null;
  title: string;
  problem: string | null;
  audience_type: string | null;
  niche: string | null;
  cover_image_url?: string | null;
}

export interface PublicProjectAuthor {
  id: number;
  username: string | null;
  avatar_url: string | null;
}

export interface ProjectDetailRow {
  label: string;
  value: string;
}

export interface PublicProject {
  id: string;
  slug: string | null;
  public_id: string;
  public_url: string;
  published_at: string | null;
  show_contacts: boolean;
  cover_image_url?: string | null;
  title: string;
  problem: string | null;
  audience_type: string | null;
  niche: string | null;
  what_done: string | null;
  stack: string | null;
  project_status?: string | null;
  dev_time: string | null;
  potential: string | null;
  goal: string | null;
  inbound_ready?: string | null;
  cool_part?: string | null;
  hardest_part?: string | null;
  monetization?: string | null;
  links?: string[];
  author_name: string | null;
  contact_mode: string | null;
  contact_value: string | null;
  /** True when request is authenticated and current user is the project owner. */
  is_owner?: boolean;
  /** Optional fields when backend returns extended card (e.g. from showcase). */
  emoji?: string | null;
  category?: string | null;
  statuses?: string[];
  views_count?: number;
  reactions_count?: number;
  want_count?: number;
  idea_count?: number;
  invest_count?: number;
  viewer_reaction_type?: 'want' | 'idea' | 'invest' | null;
  price?: string | null;
  author?: PublicProjectAuthor | null;
  /** Project links (optional, set via wizard) */
  site_url?: string | null;
  demo_url?: string | null;
  github_url?: string | null;
  tg_url?: string | null;
  // Legacy aliases from old API versions.
  website_url?: string | null;
  repo_url?: string | null;
  // Additional non-empty fields prepared for details card rendering.
  extra_details?: ProjectDetailRow[];
}

/**
 * Submit a project for moderation.
 */
export async function submitProject(id: string, params: { show_contacts: boolean }): Promise<ProjectDetails> {
  return request<ProjectDetails>(`/projects/${id}/submit`, {
    method: 'POST',
    body: { show_contacts: params.show_contacts },
  });
}

/**
 * Withdraw a project from moderation back to draft.
 */
export async function withdrawProject(id: string): Promise<ProjectDetails> {
  return request<ProjectDetails>(`/projects/${id}/withdraw`, {
    method: 'POST',
  });
}

/**
 * Admin: unpublish a project (remove from public list).
 */
export async function adminUnpublishProject(id: string): Promise<ProjectDetails> {
  return request<ProjectDetails>(`/projects/${id}/admin-unpublish`, {
    method: 'POST',
  });
}

/**
 * Delete a project (only allowed for draft/rejected).
 */
export async function deleteProject(id: string): Promise<void> {
  await request(`/projects/${id}`, {
    method: 'DELETE',
  });
}

/**
 * List public published projects.
 */
export async function getPublicProjects(params: { limit?: number; offset?: number } = {}): Promise<PublicProjectListItem[]> {
  const limit = typeof params.limit === 'number' ? params.limit : 50;
  const offset = typeof params.offset === 'number' ? params.offset : 0;
  return request<PublicProjectListItem[]>(`/public/projects?limit=${limit}&offset=${offset}`, {
    skipAuth: true,
  });
}

/**
 * Get a public published project by id_or_slug.
 * Sends auth token when present so backend can return is_owner.
 */
export async function getPublicProject(idOrSlug: string): Promise<PublicProject> {
  const encoded = encodeURIComponent(String(idOrSlug || '').trim());
  return request<PublicProject>(`/public/projects/${encoded}`);
}

/**
 * Record view event for a published project (fire-and-forget).
 * POST /public/projects/{id_or_slug}/view. No auth required.
 */
export async function postProjectView(idOrSlug: string): Promise<void> {
  const encoded = encodeURIComponent(String(idOrSlug || '').trim());
  await request<unknown>(`/public/projects/${encoded}/view`, {
    method: 'POST',
  });
}

/**
 * Unified marketplace lead.
 * POST /leads with duplicate protection (24h).
 */
export type LeadType = 'want' | 'idea' | 'invest' | 'response';

export interface CreateLeadPayload {
  type: LeadType;
  project_id?: string;
  request_id?: string;
}

export interface CreateLeadResponse {
  status: 'created' | 'duplicate';
  lead_id?: string | null;
  duplicate_until?: string | null;
}

export async function postLead(payload: CreateLeadPayload): Promise<CreateLeadResponse> {
  return request<CreateLeadResponse>('/leads', {
    method: 'POST',
    body: payload,
  });
}

export interface LeadInboxActor {
  id: number;
  username: string | null;
  full_name: string | null;
}

export interface LeadInboxItem {
  id: string;
  type: string;
  type_label: string;
  created_at: string;
  actor: LeadInboxActor;
}

export interface LeadInboxProjectGroup {
  project_id: string;
  title: string;
  leads_count: number;
  last_lead_at: string;
  leads: LeadInboxItem[];
}

export interface LeadInboxRequestGroup {
  request_id: string;
  title: string;
  status: string;
  leads_count: number;
  last_lead_at: string;
  leads: LeadInboxItem[];
}

export interface LeadInboxResponse {
  project_groups: LeadInboxProjectGroup[];
  request_groups: LeadInboxRequestGroup[];
}

export async function fetchAuthorLeadsInbox(): Promise<LeadInboxResponse> {
  if (!API_BASE_URL) {
    return {
      project_groups: [],
      request_groups: [],
    };
  }
  return request<LeadInboxResponse>('/leads/inbox');
}

/**
 * Send reaction to a project.
 * Uses the canonical reaction endpoint with first-write-wins semantics.
 */
export async function reactToProject(projectId: string, type: string): Promise<void> {
  await request(`/projects/${encodeURIComponent(String(projectId || '').trim())}/react`, {
    method: 'POST',
    body: {
      type: String(type || '').trim(),
    },
  });
}

export interface OwnerContact {
  telegram_id: number;
  username: string | null;
  first_name: string | null;
}

export interface ContactClickResponse {
  owner_contact: OwnerContact;
}

/**
 * Log contact click and get owner contact. Requires auth.
 * 400 if viewer is owner (caller should hide button).
 */
export async function postProjectContactClick(projectId: string): Promise<ContactClickResponse> {
  return request<ContactClickResponse>(`/projects/${encodeURIComponent(projectId)}/contact-click`, {
    method: 'POST',
  });
}

export interface SimilarRequestPayload {
  message: string;
  budget?: string | null;
  deadline?: string | null;
  contact_preference?: string | null;
}

export interface SimilarRequestResponse {
  ok: true;
  request_id: string;
}

/**
 * Create "Order similar" lead for a published project. Requires auth.
 * 400 if owner sends to own project or message &lt; 20 chars; 404 if project not found.
 */
export async function postSimilarRequest(
  projectId: string,
  payload: SimilarRequestPayload
): Promise<SimilarRequestResponse> {
  return request<SimilarRequestResponse>(
    `/projects/${encodeURIComponent(projectId)}/similar-request`,
    {
      method: 'POST',
      body: {
        message: payload.message.trim(),
        budget: payload.budget?.trim() || undefined,
        deadline: payload.deadline?.trim() || undefined,
        contact_preference: payload.contact_preference?.trim() || undefined,
      },
    }
  );
}

// =============================================================================
// Requests (Заявки) — лента заявок, детальная, создание, отклик
// Когда бэкенд добавит /requests — заменить заглушки на реальные вызовы.
// =============================================================================

export type RequestType =
  | 'partner'
  | 'investor'
  | 'developer'
  | 'case'
  | 'opensource'
  | 'in_development'
  // Legacy aliases
  | 'development'
  | 'invest';
export type RequestStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'needs_fix';

export interface RequestAuthor {
  id: number;
  username: string | null;
  avatar_url: string | null;
}

export interface RequestListItem {
  id: string;
  title: string;
  author: RequestAuthor;
  published_at: string;
  type: RequestType;
  type_label: string;
  description: string;
  tools: string[];
  budget: string | null;
  deadline: string | null;
  format: string | null;
  urgent: boolean;
  responses_count: number;
  views_count?: number;
  /** Текущий пользователь уже откликнулся */
  i_responded?: boolean;
  status: RequestStatus;
  rejected_reason?: string | null;
  needs_fix_comment?: string | null;
}

export interface RequestDetail extends RequestListItem {
  task: string;
  what_matters: string[];
  responses: RequestResponse[];
}

export interface RequestResponse {
  id: string;
  author: RequestAuthor;
  summary: string | null;
  rating: number | null;
  is_me?: boolean;
}

export interface CreateRequestPayload {
  type: RequestType;
  title: string;
  description: string;
  budget: string | null;
  deadline: string | null;
  format: string | null;
  what_matters: string[];
  tools: string[];
  urgent: boolean;
}

const REQUESTS_STUB: RequestListItem[] = [];

function normalizeRequestType(type: RequestType): RequestType {
  if (type === 'development') return 'developer';
  if (type === 'invest') return 'investor';
  return type;
}

/** Лента заявок (фильтр по типу). */
export async function fetchRequests(params: { type?: RequestType | null } = {}): Promise<RequestListItem[]> {
  const normalizedType = params.type ? normalizeRequestType(params.type) : null;
  const stub = normalizedType ? REQUESTS_STUB.filter((r) => normalizeRequestType(r.type) === normalizedType) : REQUESTS_STUB;
  if (!API_BASE_URL) return stub;
  try {
    const q = new URLSearchParams();
    if (normalizedType) q.set('type', normalizedType);
    const list = await request<RequestListItem[]>(`/requests?${q.toString()}`);
    return Array.isArray(list) ? list : stub;
  } catch {
    return stub;
  }
}

/** Мои заявки (все статусы). */
export async function fetchMyRequests(): Promise<RequestListItem[]> {
  if (!API_BASE_URL) return REQUESTS_STUB;
  const list = await request<RequestListItem[]>('/my/requests');
  return Array.isArray(list) ? list : [];
}

/** Очередь модерации (admin). */
export async function fetchAdminRequests(status: RequestStatus = 'submitted'): Promise<RequestListItem[]> {
  if (!API_BASE_URL) return [];
  const q = new URLSearchParams();
  q.set('status', status);
  return request<RequestListItem[]>(`/admin/requests?${q.toString()}`);
}

/** Детальная заявка. Пока заглушка. */
export async function getRequest(id: string): Promise<RequestDetail | null> {
  if (!API_BASE_URL) {
    const item = REQUESTS_STUB.find((r) => r.id === id);
    if (!item) return null;
    return {
      ...item,
      task: item.description,
      what_matters: [],
      responses: [],
    };
  }
  try {
    return await request<RequestDetail>(`/requests/${encodeURIComponent(id)}`);
  } catch {
    const item = REQUESTS_STUB.find((r) => r.id === id);
    if (!item) return null;
    return { ...item, task: item.description, what_matters: [], responses: [] };
  }
}

/** Создать заявку. Без API — заглушка; с API — пробрасывает ошибки наружу. */
export async function createRequest(payload: CreateRequestPayload): Promise<RequestListItem> {
  const normalizedType = normalizeRequestType(payload.type);
  if (!API_BASE_URL) {
    const newItem: RequestListItem = {
      id: `req-${Date.now()}`,
      title: payload.title,
      author: { id: 0, username: null, avatar_url: null },
      published_at: new Date().toISOString(),
      type: normalizedType,
      type_label: normalizedType === 'partner' ? 'Партнёр' : normalizedType === 'investor' ? 'Инвестор' : 'Разработка',
      description: payload.description.slice(0, 200),
      tools: payload.tools,
      budget: payload.budget,
      deadline: payload.deadline,
      format: payload.format,
      urgent: payload.urgent,
      responses_count: 0,
      i_responded: false,
      status: 'draft',
    };
    REQUESTS_STUB.unshift(newItem);
    return newItem;
  }
  return request<RequestListItem>('/requests', {
    method: 'POST',
    body: {
      type: normalizedType,
      title: payload.title,
      task: payload.description,
      budget: payload.budget,
      deadline: payload.deadline,
      format: payload.format,
      what_matters: payload.what_matters,
      tools: payload.tools,
      urgent: payload.urgent,
    },
  });
}

/** Редактировать заявку (draft/needs_fix). */
export async function updateRequest(id: string, payload: Partial<CreateRequestPayload>): Promise<RequestListItem> {
  if (!API_BASE_URL) {
    const current = REQUESTS_STUB.find((r) => r.id === id);
    if (!current) throw new Error('Заявка не найдена');
    if (payload.type !== undefined) {
      current.type = normalizeRequestType(payload.type);
    }
    current.title = payload.title ?? current.title;
    current.description = payload.description ?? current.description;
    current.budget = payload.budget ?? current.budget;
    current.deadline = payload.deadline ?? current.deadline;
    current.format = payload.format ?? current.format;
    current.tools = payload.tools ?? current.tools;
    current.urgent = payload.urgent ?? current.urgent;
    return current;
  }
  return request<RequestListItem>(`/requests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: {
      ...(payload.type !== undefined ? { type: normalizeRequestType(payload.type) } : {}),
      ...(payload.title !== undefined ? { title: payload.title } : {}),
      ...(payload.description !== undefined ? { task: payload.description } : {}),
      ...(payload.budget !== undefined ? { budget: payload.budget } : {}),
      ...(payload.deadline !== undefined ? { deadline: payload.deadline } : {}),
      ...(payload.format !== undefined ? { format: payload.format } : {}),
      ...(payload.what_matters !== undefined ? { what_matters: payload.what_matters } : {}),
      ...(payload.tools !== undefined ? { tools: payload.tools } : {}),
      ...(payload.urgent !== undefined ? { urgent: payload.urgent } : {}),
    },
  });
}

/** Отправить заявку на модерацию. */
export async function submitRequest(id: string): Promise<RequestListItem> {
  if (!API_BASE_URL) {
    const current = REQUESTS_STUB.find((r) => r.id === id);
    if (!current) throw new Error('Заявка не найдена');
    current.status = 'submitted';
    return current;
  }
  return request<RequestListItem>(`/requests/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
  });
}

export async function deleteRequest(id: string): Promise<void> {
  if (!API_BASE_URL) {
    const index = REQUESTS_STUB.findIndex((item) => item.id === id);
    if (index === -1) throw new Error('Заявка не найдена');
    REQUESTS_STUB.splice(index, 1);
    return;
  }
  await request(`/requests/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function approveRequest(id: string): Promise<RequestListItem> {
  return request<RequestListItem>(`/admin/requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
}

export async function rejectRequest(id: string, reason: string): Promise<RequestListItem> {
  return request<RequestListItem>(`/admin/requests/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: { reason },
  });
}

export async function needsFixRequest(id: string, comment: string): Promise<RequestListItem> {
  return request<RequestListItem>(`/admin/requests/${encodeURIComponent(id)}/needs-fix`, {
    method: 'POST',
    body: { comment },
  });
}

/** Откликнуться на заявку через unified POST /leads. */
export async function respondToRequest(id: string): Promise<CreateLeadResponse> {
  if (!API_BASE_URL) return { status: 'created' };
  return postLead({
    type: 'response',
    request_id: String(id || '').trim(),
  });
}

// =============================================================================
// Public Profile
// =============================================================================

export interface PublicProfile {
  user_id: number;
  telegram_id: number;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string;
  selected_role: ProfileRole | null;
  completion_percent: number;
  profile_completeness: number;
  next_completion_hint: string | null;
  streak_days: number;
  project_count: number;
  projects_count: number;
  requests_count: number;
  successful_referrals_count: number;
  total_reactions_received: number;
  badges: ProfileBadge[];
}

export async function getPublicProfile(telegramId: number): Promise<PublicProfile> {
  return request<PublicProfile>(`/profile/${telegramId}`, { skipAuth: true });
}

export interface PublicUserProfile {
  id: number;
  telegram_id: number;
  display_name: string;
  username: string | null;
  avatar_url: string | null;
  bio: string;
  selected_role: ProfileRole | null;
  completion_percent: number;
  profile_completeness: number;
  next_completion_hint: string | null;
  streak_days: number;
  projects_count: number;
  reactions_count: number;
  requests_count: number;
  successful_referrals_count: number;
  badges: ProfileBadge[];
}

export async function getPublicUser(userId: number): Promise<PublicUserProfile> {
  return request<PublicUserProfile>(`/users/${userId}`, { skipAuth: true });
}

// =============================================================================
// Debug API (no-auth endpoints)
// =============================================================================

export interface ClientLogResponse {
  ok: true;
  request_id: string;
  received_at: string;
}

/**
 * Send client-side logs/diagnostics to the server for debugging.
 * Must NOT include secrets (initData, tokens, cookies).
 */
export async function postClientLog(payload: unknown): Promise<ClientLogResponse> {
  return request<ClientLogResponse>('/debug/client-log', {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}


// =============================================================================
// Phase 4 — Gamification
// =============================================================================

export type ProfileRole = 'Разработчик' | 'Инвестор' | 'Заказчик' | 'Наблюдатель';

export interface ProfileBadge {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: string;
  xp_reward: number;
  earned: boolean;
  earned_at: string | null;
}

export interface GamificationProfile {
  user_id?: number | null;
  telegram_id: number;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string;
  selected_role: ProfileRole | null;
  niche: string;
  completion_percent: number;
  profile_completeness: number;
  next_completion_hint: string | null;
  streak_days: number;
  level: number;
  xp: number;
  project_count: number;
  projects_count: number;
  requests_count: number;
  successful_referrals_count: number;
  referral_count: number;
  total_reactions_received: number;
  badges: ProfileBadge[];
}

export interface StreakResult {
  streak: number;
  is_new_day: boolean;
}

export interface BadgeItem {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: string;
  xp_reward: number;
  earned: boolean;
  earned_at: string | null;
}

export interface NewBadge {
  new_badge_id: string;
  new_badge_name: string;
  new_badge_emoji: string;
}

export interface NotificationSettings {
  reactions_enabled: boolean;
  requests_enabled: boolean;
  weekly_digest_enabled: boolean;
}

export async function getGamificationProfile(): Promise<GamificationProfile> {
  return request<GamificationProfile>('/me/profile');
}

export async function updateGamificationProfile(data: {
  bio?: string;
  selected_role?: ProfileRole | '';
  niche?: string;
}): Promise<GamificationProfile> {
  return request<GamificationProfile>('/me/profile', { method: 'PATCH', body: data });
}

export async function processVisit(): Promise<StreakResult> {
  return request<StreakResult>('/me/visit', { method: 'POST' });
}

export async function getBadges(): Promise<BadgeItem[]> {
  return request<BadgeItem[]>('/me/badges');
}

export async function checkBadges(): Promise<NewBadge[]> {
  return request<NewBadge[]>('/me/badges/check', { method: 'POST' });
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  return request<NotificationSettings>('/me/notification-settings');
}

export async function updateNotificationSettings(
  settings: NotificationSettings,
): Promise<NotificationSettings> {
  return request<NotificationSettings>('/me/notification-settings', {
    method: 'PUT',
    body: settings,
  });
}
